import type { MouseEventHandler, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, sets aria-busy, and disables activation. */
  loading?: boolean;
  disabled?: boolean;
  /** Render element. Defaults to `button`; use `a` (with `href`) for link-buttons. */
  as?: 'button' | 'a';
  href?: string;
  target?: string;
  rel?: string;
  type?: 'button' | 'submit' | 'reset';
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  id?: string;
  name?: string;
  'aria-label'?: string;
  /** Native tooltip, e.g. explaining what an action does on hover/focus. */
  title?: string;
  className?: string;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  as,
  href,
  target,
  rel,
  type = 'button',
  onClick,
  id,
  name,
  'aria-label': ariaLabel,
  title,
  className,
  children,
}: ButtonProps) {
  const isInactive = disabled || loading;
  const classes = [
    'nuff-btn',
    `nuff-btn--${variant}`,
    `nuff-btn--${size}`,
    loading && 'nuff-btn--loading',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {loading && <span className="nuff-btn__spinner" aria-hidden="true" />}
      {children}
    </>
  );

  const renderAnchor = as === 'a' || (as === undefined && href !== undefined);

  if (renderAnchor) {
    return (
      <a
        className={classes}
        // Drop href when inactive so the link cannot be activated.
        {...(href !== undefined && !isInactive ? { href } : {})}
        {...(target ? { target } : {})}
        {...(rel ? { rel } : {})}
        {...(id ? { id } : {})}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        {...(title ? { title } : {})}
        {...(isInactive ? { 'aria-disabled': true } : {})}
        {...(loading ? { 'aria-busy': true } : {})}
        onClick={onClick}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      className={classes}
      type={type}
      disabled={isInactive}
      {...(id ? { id } : {})}
      {...(name ? { name } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      {...(title ? { title } : {})}
      {...(loading ? { 'aria-busy': true } : {})}
      onClick={onClick}
    >
      {content}
    </button>
  );
}
